<template>
  <div
    v-if="starsAverage > 0"
    class="product-rating"
    :class="{ 'is-ready': starsReady, 'product-rating--show-cursor': showCursor }"
    :style="ratingProperty"
  >
    <span
      class="visually-hidden"
      v-text="ratingLabel"
    />

    <span
      v-if="starsAverage && showRatingText"
      class="product-rating__total text-body-xs-regular"
      v-text="starsAverage"
    />

    <span
      v-if="ratingTotal"
      class="product-rating__total text-body-xs-regular"
      v-text="ratingTotal"
    />
  </div>
</template>

<script>

/**
 * Vue: Product rating (product-rating)
 * -----------------------------------------------------------------------------
 * Trustpilot rating displayed in product card and PDP.
 * - Component settings: snippet / dynamic / async / scroll
 *
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {Boolean} showCursor - Show cursor on hover.
 *
 */
import getTrustpilotData from '~/helpers/trustpilot'

export default {
  name: 'ProductRating',

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    showRatingText: {
      type: Boolean,
      default: false,
    },
    visible: {
      type: Boolean,
      default: true,
    },
    showCursor: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      ratingData: false,
      starsReady: false,
    }
  },

  computed: {

    /**
     * Computed the CSS Variable for the star ratings.
     */
    ratingProperty() {
      if (!this.product || !this.ratingData) {
        return ''
      }

      return `--rating: ${this.starsAverage};`
    },

    /**
     * Get the accessible text for the star rating.
     * @returns {String}
     */
    ratingLabel() {
      if (!this.product || !this.ratingData) {
        return ''
      }

      return this.$string('accessibility.rating_label', {
        replace: { stars: this.starsAverage },
      })
    },

    /**
     * Get the total number of Trustpilot reviews.
     * @returns {String}
     */
    ratingTotal() {
      if (!this.product || !this.ratingData) {
        return ''
      }

      const total = this.ratingData.total
        ? this.ratingData.total
        : this.ratingData.totalImported
      return `(${total})`
    },

    /**
     * Get average star rating from Trustpilot reviews.
     * @returns {Number}
     */
    starsAverage() {
      if (!this.product || !this.ratingData) {
        return 0
      }

      return this.ratingData.trustpilot
        ? this.ratingData.trustpilot
        : this.ratingData.imported
    },
  },

  watch: {

    /**
     * Watch for product being loaded.
     * - Load reviews from trustpilot.
     */
    product() {
      this.setRatingData()
    },

    /**
     * Watch the visible prop and fetch rating data once card is in view.
     */
    visible() {
      this.setRatingData()
    },
  },

  async mounted() {
    await this.setRatingData()
  },

  methods: {

    /**
     * Set ratingData based on Trustpilot API results.
     * - Trustpilot data should override any legacy data.
     */
    async setRatingData() {
      if (!this.visible) {
        return
      }

      /**
       * Map all variant skus for personalised products, which have different
       * skus depending on the number of characters used.
       */
      const productSkus = this.product?.variants?.map((variant) => variant.sku)

      if (!productSkus || !productSkus.length) {
        return
      }

      try {
        this.ratingData = await getTrustpilotData(productSkus)
        await this.$nextTick()
        this.starsReady = true

      } catch (error) {
        cnvs.ReportError('Failed to load trustpilot', error)
      }
    },
  },
}
</script>

<style lang="scss">
@import './product-rating';
</style>
